import classNames from 'classnames';
import React, { useState } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';
import { cn } from '../../utils/classUtils';
import ControlledInputLabelComponent from './ControlledInputLabelComponent';
import FieldErrorMessage from './FieldErrorMessage';

export type ControlledTextAreaInputType =
  | 'text'
  | 'email'
  | 'number'
  | 'hidden'
  | 'date'
  | 'datetime-local'
  | 'image'
  | 'month'
  | 'password'
  | 'search'
  | 'tel'
  | 'time'
  | 'url'
  | 'week';

export type ControlledTextAreaInputModeProps =
  | 'none'
  | 'text'
  | 'tel'
  | 'url'
  | 'email'
  | 'numeric'
  | 'decimal'
  | 'search';

export type ControlledTextAreaInputDirection = 'rtl' | 'ltr';
export const VALID_VARIANTS = ['default'] as const;
type ControlledTextAreaInputVariantTuple = typeof VALID_VARIANTS;
type ControlledTextAreaInputVariant =
  ControlledTextAreaInputVariantTuple[number];

type variantMapStyles<T> = {
  [x in ControlledTextAreaInputVariant]: T;
};

export interface ControlledTextAreaInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName> {
  disabled?: boolean;
  variant?: ControlledTextAreaInputVariant;
  startAdornment?: React.ReactElement;
  endAdornment?: React.ReactElement;
  scrollEnabled?: boolean;
  maxLength?: number;
  placeholder?: string;
  type?: ControlledTextAreaInputType;
  inputMode?: ControlledTextAreaInputModeProps;
  direction?: ControlledTextAreaInputDirection;
  label?: string;
  subLabel?: string;
  readOnly?: boolean;
  noBorder?: boolean;
  hideErrorMessage?: boolean;
  isRequired?: boolean;
  helperText?: string;
  disableCopyPaste?: boolean;
  isPassword?: boolean;
  autoComplete?: string | undefined;
}

const ControlledTextAreaInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  disabled = false,
  variant = 'default',
  shouldUnregister = true,
  startAdornment,
  endAdornment,
  scrollEnabled = true,
  maxLength,
  placeholder,
  label,
  subLabel,
  type = 'text',
  inputMode = 'text',
  direction = 'ltr',
  readOnly,
  noBorder,
  hideErrorMessage = false,
  helperText,
  isRequired = false,
  disableCopyPaste = false,
  isPassword,
  autoComplete,
  ...controllerProps
}: ControlledTextAreaInputProps<TFieldValues, TName>) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const {
    field: { value, onChange, name, onBlur },
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    ...controllerProps,
    shouldUnregister,
  });

  const isShowLabel = !!label && (isFocused || !!value);

  const placeholderText = isFocused ? '' : placeholder;

  const variantBorderStylesMap: variantMapStyles<string> = {
    default: classNames([
      'flex flex-row items-center justify-start overflow-hidden border rounded-lg',
      isFocused ? 'border-black' : 'border-silver',
    ]),
  };

  const variantViewStylesMap: variantMapStyles<string> = {
    default: classNames([
      'flex flex-row justify-start items-center w-full',
      readOnly || disabled ? 'bg-whitesmoke' : 'bg-transparent',
    ]),
  };

  const textInputStylesMap: variantMapStyles<string> = {
    default:
      'flex items-center justify-center flex-grow font-inter-regular text-base px-2 py-3 leading-4 text-black bg-transparent appearance-none border-none focus:outline-none focus:ring-0',
  };

  const renderIconOrEndAdornment = (): React.ReactElement => {
    if (isPassword || ((isFocused || !value) && endAdornment)) {
      return <div className='pr-2'>{endAdornment}</div>;
    }

    return <div />;
  };

  const handleOnBlur = () => {
    if (typeof value === 'string') {
      onChange(value.trim());
    }
    onBlur();
    setIsFocused(false);
  };

  return (
    <div>
      {isShowLabel && <ControlledInputLabelComponent label={label!} />}
      <div className={variantBorderStylesMap[variant]}>
        <div
          data-testid='input-parent'
          className={variantViewStylesMap[variant]}
        >
          {startAdornment && <div className='pl-2'>{startAdornment}</div>}
          <textarea
            name={name}
            className={classNames([
              textInputStylesMap[variant],
              'text-sm leading-5',
            ])}
            style={
              {
                fieldSizing: 'content',
              } as React.CSSProperties
            }
            inputMode={inputMode}
            dir={direction}
            readOnly={readOnly}
            disabled={disabled}
            onCopy={(e) => {
              if (disableCopyPaste) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              if (disableCopyPaste) {
                e.preventDefault();
              }
            }}
            onFocus={() => setIsFocused(true)}
            onChange={onChange}
            onBlur={handleOnBlur}
            placeholder={placeholderText!}
            value={value}
            autoComplete={autoComplete}
            data-testid={`input-${label}`}
            maxLength={maxLength}
          />
          {renderIconOrEndAdornment()}
        </div>
      </div>
      <div className='flex flex-row items-center justify-between mt-1'>
        <FieldErrorMessage message={error?.message} />
        {maxLength && !error?.message && (
          <div
            className={cn('text-sm text-gray-500', {
              'text-red-500': value?.length && value?.length === maxLength,
            })}
          >
            {value?.length || 0}/{maxLength} characters
          </div>
        )}
      </div>
    </div>
  );
};

export default ControlledTextAreaInput;
